'use client';

import { usePublicAnalytics } from '@/lib/analytics/use-public-analytics';

import { Suspense, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';
import {
  COOKIE_CONSENT_CHANGED_EVENT,
  isPostHogEnabledForPathname,
  isAnalyticsCookieConsentAccepted,
} from '@/lib/analytics/posthog-paths';

let posthogInitialized = false;

/**
 * PostHog for the public marketing site: pageviews, autocapture, session replay,
 * and anonymous distinct IDs (unique + returning user metrics in PostHog).
 * Gated on cookie consent and path (see `posthog-paths.ts`).
 */
function PostHogPageView() {
  const publicAnalytics = usePublicAnalytics();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [consentOk, setConsentOk] = useState(false);

  useEffect(() => {
    const read = () => setConsentOk(isAnalyticsCookieConsentAccepted());
    read();
    if (typeof window === 'undefined') return undefined;
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, read);
    return () =>
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, read);
  }, []);

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
    if (!key) return;

    const accepted =
      consentOk || (typeof window !== 'undefined' && isAnalyticsCookieConsentAccepted());
    if (!publicAnalytics || !accepted) {
      if (posthogInitialized) {
        posthog.reset();
        posthog.opt_out_capturing();
      }
      return;
    }

    if (!isPostHogEnabledForPathname(pathname)) {
      if (posthogInitialized) {
        posthog.opt_out_capturing();
      }
      return;
    }

    const apiHost =
      process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

    if (!posthogInitialized) {
      const uiHost = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST;
      posthog.init(key, {
        api_host: apiHost,
        ...(uiHost ? { ui_host: uiHost } : {}),
        person_profiles: 'always',
        capture_pageview: false,
        capture_pageleave: true,
        persistence: 'localStorage+cookie',
        session_recording: {
          maskAllInputs: true,
        },
        autocapture: true,
        defaults: '2026-01-30',
      });
      posthogInitialized = true;
    } else {
      posthog.opt_in_capturing();
    }

    posthog.capture('$pageview', {
      $current_url: window.location.href,
    });
  }, [pathname, searchParams, consentOk, publicAnalytics]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
    </>
  );
}
